<template>
  <div
    ref="rootEl"
    class="m-calendar-grid"
    role="dialog"
    aria-label="Date picker"
    @keydown.esc.stop.prevent="emit('close')"
  >
    <div class="m-calendar-grid__nav">
      <button
        type="button"
        class="m-calendar-grid__nav-button"
        aria-label="Go to previous month"
        @click="prevMonth"
      >
        <i class="fa-solid fa-chevron-left" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="m-calendar-grid__label m-calendar-grid__label--button"
        data-testid="month-label"
        :aria-label="`Change the current month: ${monthLabel}`"
        aria-haspopup="dialog"
        :aria-expanded="view === 'months'"
        @click="toggleView('months')"
      >
        {{ monthLabel }}
      </button>
      <button
        type="button"
        class="m-calendar-grid__nav-button"
        aria-label="Go to next month"
        @click="nextMonth"
      >
        <i class="fa-solid fa-chevron-right" aria-hidden="true" />
      </button>

      <button
        type="button"
        class="m-calendar-grid__nav-button"
        :aria-label="view === 'years' ? 'Go to previous years' : 'Go to previous year'"
        @click="prevYear"
      >
        <i class="fa-solid fa-chevron-left" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="m-calendar-grid__label m-calendar-grid__label--button"
        data-testid="year-label"
        :aria-label="`Change the current year: ${viewYear}`"
        aria-haspopup="dialog"
        :aria-expanded="view === 'years'"
        @click="toggleView('years')"
      >
        {{ viewYear }}
      </button>
      <button
        type="button"
        class="m-calendar-grid__nav-button"
        :aria-label="view === 'years' ? 'Go to next years' : 'Go to next year'"
        @click="nextYear"
      >
        <i class="fa-solid fa-chevron-right" aria-hidden="true" />
      </button>

      <span class="m-calendar-grid__nav-spacer" />
      <slot name="header-actions" />
    </div>

    <template v-if="view === 'days'">
      <div class="m-calendar-grid__weekdays">
        <span v-for="(wd, i) in weekdaysShort" :key="wd" class="m-calendar-grid__weekday">
          <span aria-hidden="true">{{ wd }}</span>
          <span class="sr-only">{{ weekdaysFull[i] }}</span>
        </span>
      </div>

      <div
        class="m-calendar-grid__days"
        role="grid"
        :aria-label="`${monthLabel} ${viewYear}`"
        @keydown="onGridKeydown"
      >
        <div v-for="(week, wi) in calendar" :key="wi" class="m-calendar-grid__row" role="row">
          <span
            v-for="(day, di) in week"
            :key="`${wi}-${di}`"
            class="m-calendar-grid__cell"
            role="gridcell"
          >
            <button
              v-if="day"
              type="button"
              class="m-calendar-grid__day"
              :class="{
                'm-calendar-grid__day--selected': isSelected(day),
                'm-calendar-grid__day--today': isToday(day),
              }"
              :data-day="day"
              :data-focus-first="day === focusedDay ? '' : undefined"
              :tabindex="day === focusedDay ? 0 : -1"
              :disabled="isDisabled(day)"
              :aria-pressed="isSelected(day)"
              @click="select(day)"
              @focus="focusedDay = day"
            >
              <span aria-hidden="true">{{ day }}</span>
              <span class="sr-only">{{ dayLabel(day) }}</span>
            </button>
          </span>
        </div>
      </div>
    </template>

    <div
      v-else-if="view === 'months'"
      class="m-calendar-grid__chooser"
      data-testid="month-chooser"
      @keydown="onChooserKeydown($event, 12)"
    >
      <button
        v-for="(m, i) in monthsShort"
        :key="m"
        type="button"
        class="m-calendar-grid__chooser-item"
        :class="{ 'm-calendar-grid__chooser-item--selected': i === viewMonth }"
        :aria-label="monthsFull[i]"
        @click="selectMonth(i)"
      >
        {{ m }}
      </button>
    </div>

    <div
      v-else
      class="m-calendar-grid__chooser"
      data-testid="year-chooser"
      @keydown="onChooserKeydown($event, yearPage.length)"
    >
      <button
        v-for="y in yearPage"
        :key="y"
        type="button"
        class="m-calendar-grid__chooser-item"
        :class="{ 'm-calendar-grid__chooser-item--selected': y === viewYear }"
        @click="selectYear(y)"
      >
        {{ y }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import "./MCalendarGrid.scss";
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import type { MCalendarGridProps } from "./MCalendarGrid.props";
import { mCalendarGridDefaults } from "./MCalendarGrid.props";
import { addOrdinalSuffix, formatDatePattern, isWithinRange } from "../../../../utils/date";
import { useFocusTrap } from "../../../../composables/useFocusTrap";

const props = withDefaults(defineProps<MCalendarGridProps>(), mCalendarGridDefaults);

const emit = defineEmits<{
  (e: "update:modelValue", value: string | null): void;
  (e: "navigation", value: { year: number; month: number }): void;
  // emitted only when a month is explicitly picked in the chooser (not on year paging) — used by the month picker
  (e: "monthSelected", value: { year: number; month: number }): void;
  (e: "close"): void;
}>();

const monthsFull = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const monthsShort = monthsFull.map((m) => m.slice(0, 3));
const weekdaysShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const weekdaysFull = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

type View = "days" | "months" | "years";
const view = ref<View>(props.initialView ?? "days");
const YEAR_PAGE_SIZE = 12;
const yearPageStart = ref(0);

const rootEl = ref<HTMLElement | null>(null);

// "DD/MM/YYYY" -> Date | null
function parseModel(value?: string | null): Date | null {
  if (!value) return null;
  const [d, m, y] = value.split("/").map((x) => +x);
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
}

function toDate(value: Date | string | number | undefined, fallback: Date): Date {
  if (value == null) return fallback;
  return value instanceof Date ? value : new Date(value);
}

const selectedDate = computed(() => parseModel(props.modelValue));

const today = new Date();
const initial = selectedDate.value ?? today;
const viewYear = ref(initial.getFullYear());
const viewMonth = ref(initial.getMonth());

// the day that holds keyboard focus within the current month (roving tabindex)
const initialFocus =
  selectedDate.value && selectedDate.value.getMonth() === viewMonth.value
    ? selectedDate.value.getDate()
    : today.getMonth() === viewMonth.value && today.getFullYear() === viewYear.value
    ? today.getDate()
    : 1;
const focusedDay = ref(initialFocus);

// ENG-21073 The calendar is the active element's logical child, so capture the trigger
// (the button that opened it) now and restore focus to it on close. useFocusTrap's own
// returnFocus restores focus-history queue[0] — the OLDEST focused element app-wide — which
// lands outside any surrounding MMenu and collapses the whole filter popover on day-select.
const triggerEl = (typeof document !== "undefined" ? document.activeElement : null) as
  | HTMLElement
  | null
  | undefined;

// focus-on-open + focus-trap; we own return-focus (see triggerEl above), so disable the trap's.
useFocusTrap(rootEl, true, false);

onBeforeUnmount(() => triggerEl?.focus?.());

// keep the visible month in sync when the selected value changes externally
watch(selectedDate, (d) => {
  if (d) {
    viewYear.value = d.getFullYear();
    viewMonth.value = d.getMonth();
    focusedDay.value = d.getDate();
  }
});

const monthLabel = computed(() => monthsFull[viewMonth.value]);

const yearPage = computed(() =>
  Array.from({ length: YEAR_PAGE_SIZE }, (_, i) => yearPageStart.value + i),
);

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

const calendar = computed<(number | null)[][]>(() => {
  const first = new Date(viewYear.value, viewMonth.value, 1);
  const leading = (first.getDay() + 6) % 7; // Monday-first leading blanks
  const total = daysInMonth(viewYear.value, viewMonth.value);

  const cells: (number | null)[] = [];
  for (let i = 0; i < leading; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
});

function isSelected(day: number): boolean {
  const d = selectedDate.value;
  return (
    !!d &&
    d.getFullYear() === viewYear.value &&
    d.getMonth() === viewMonth.value &&
    d.getDate() === day
  );
}

function isToday(day: number): boolean {
  const t = new Date();
  return (
    t.getFullYear() === viewYear.value && t.getMonth() === viewMonth.value && t.getDate() === day
  );
}

function isDisabled(day: number): boolean {
  if (props.minDate == null && props.maxDate == null) return false;
  const d = new Date(viewYear.value, viewMonth.value, day);
  const min = toDate(props.minDate, new Date(-8640000000000000));
  const max = toDate(props.maxDate, new Date(8640000000000000));
  return !isWithinRange(d, min, max, { inclusiveFrom: true, inclusiveTo: true, onlyDate: true });
}

function dayLabel(day: number): string {
  const weekday = weekdaysFull[(new Date(viewYear.value, viewMonth.value, day).getDay() + 6) % 7];
  return `${weekday} ${addOrdinalSuffix(day)}`;
}

function select(day: number): void {
  if (isDisabled(day)) return;
  if (isSelected(day)) {
    emit("update:modelValue", null);
    return;
  }
  emit(
    "update:modelValue",
    formatDatePattern(new Date(viewYear.value, viewMonth.value, day), "DD/MM/YYYY"),
  );
}

function emitNavigation(): void {
  emit("navigation", { year: viewYear.value, month: viewMonth.value });
}

// Move keyboard focus to `day` in the current month, after the grid has rendered.
function focusDay(day: number): void {
  focusedDay.value = day;
  nextTick(() => {
    rootEl.value?.querySelector<HTMLElement>(`.m-calendar-grid__day[data-day="${day}"]`)?.focus();
  });
}

// APG date-grid arrow navigation; crosses month/year boundaries.
function moveFocus(deltaDays: number): void {
  const target = new Date(viewYear.value, viewMonth.value, focusedDay.value + deltaDays);
  if (target.getMonth() !== viewMonth.value || target.getFullYear() !== viewYear.value) {
    viewYear.value = target.getFullYear();
    viewMonth.value = target.getMonth();
    emitNavigation();
  }
  focusDay(target.getDate());
}

function onGridKeydown(e: KeyboardEvent): void {
  switch (e.key) {
    case "ArrowLeft":
      moveFocus(-1);
      break;
    case "ArrowRight":
      moveFocus(1);
      break;
    case "ArrowUp":
      moveFocus(-7);
      break;
    case "ArrowDown":
      moveFocus(7);
      break;
    case "Home": // first day (Monday) of the current week
      moveFocus(-((new Date(viewYear.value, viewMonth.value, focusedDay.value).getDay() + 6) % 7));
      break;
    case "End": // last day (Sunday) of the current week
      moveFocus(
        6 - ((new Date(viewYear.value, viewMonth.value, focusedDay.value).getDay() + 6) % 7),
      );
      break;
    case "PageUp":
      e.shiftKey ? changeYear(-1) : changeMonth(-1);
      focusDay(focusedDay.value);
      break;
    case "PageDown":
      e.shiftKey ? changeYear(1) : changeMonth(1);
      focusDay(focusedDay.value);
      break;
    default:
      return;
  }
  e.preventDefault();
}

function clampFocusedDay(): void {
  const max = daysInMonth(viewYear.value, viewMonth.value);
  if (focusedDay.value > max) focusedDay.value = max;
}

// Change the displayed month/year WITHOUT moving focus (used by the nav-arrow
// buttons, so keyboard focus stays on the button). In-grid PageUp/Down calls
// focusDay() itself afterwards.
function changeMonth(delta: number): void {
  const d = new Date(viewYear.value, viewMonth.value + delta, 1);
  viewYear.value = d.getFullYear();
  viewMonth.value = d.getMonth();
  clampFocusedDay();
  emitNavigation();
}

function changeYear(delta: number): void {
  viewYear.value += delta;
  clampFocusedDay();
  emitNavigation();
}

// Month/year quick-choosers (parity with QDate's clickable month/year labels)
function toggleView(target: Exclude<View, "days">): void {
  if (view.value === target) {
    view.value = "days";
    nextTick(() => focusDay(focusedDay.value));
    return;
  }
  if (target === "years") {
    yearPageStart.value = viewYear.value - (viewYear.value % YEAR_PAGE_SIZE);
  }
  view.value = target;
  // Move keyboard focus to the selected item (or first) in the chooser after render.
  nextTick(() => {
    const el = rootEl.value;
    if (!el) return;
    const selected = el.querySelector<HTMLElement>(".m-calendar-grid__chooser-item--selected");
    const first = el.querySelector<HTMLElement>(".m-calendar-grid__chooser-item");
    (selected ?? first)?.focus();
  });
}

// Arrow-key navigation within a chooser grid (3 columns).
function onChooserKeydown(e: KeyboardEvent, total: number): void {
  const all = Array.from(
    rootEl.value?.querySelectorAll<HTMLElement>(".m-calendar-grid__chooser-item") ?? [],
  );
  const idx = all.indexOf(document.activeElement as HTMLElement);
  if (idx === -1) return;

  let next = idx;
  switch (e.key) {
    case "ArrowLeft":
      next = Math.max(0, idx - 1);
      break;
    case "ArrowRight":
      next = Math.min(total - 1, idx + 1);
      break;
    case "ArrowUp":
      next = Math.max(0, idx - 3);
      break;
    case "ArrowDown":
      next = Math.min(total - 1, idx + 3);
      break;
    case "Home":
      next = 0;
      break;
    case "End":
      next = total - 1;
      break;
    default:
      return;
  }
  e.preventDefault();
  all[next]?.focus();
}

function selectMonth(month: number): void {
  viewMonth.value = month;
  view.value = "days";
  clampFocusedDay();
  // return focus to the grid so keyboard flow continues after a chooser pick
  focusDay(focusedDay.value);
  emitNavigation();
  emit("monthSelected", { year: viewYear.value, month });
}

function selectYear(year: number): void {
  viewYear.value = year;
  view.value = "days";
  clampFocusedDay();
  // return focus to the grid so keyboard flow continues after a chooser pick
  focusDay(focusedDay.value);
  emitNavigation();
}

function prevMonth(): void {
  changeMonth(-1);
}
function nextMonth(): void {
  changeMonth(1);
}
function prevYear(): void {
  if (view.value === "years") {
    yearPageStart.value -= YEAR_PAGE_SIZE;
    return;
  }
  changeYear(-1);
}
function nextYear(): void {
  if (view.value === "years") {
    yearPageStart.value += YEAR_PAGE_SIZE;
    return;
  }
  changeYear(1);
}

defineExpose({ viewYear, viewMonth, view, focusedDay });
</script>
